import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

const FilePreviewModal = () => {
  const [data, setData] = useState(null);
  const [text, setText] = useState('');

  useEffect(() => {
    // Listens for the openFilePreview event that api.js dispatches.
    const handleOpen = (e) => {
      setData(e.detail);
    };

    window.addEventListener('openFilePreview', handleOpen);
    return () => window.removeEventListener('openFilePreview', handleOpen);
  }, []);

  const isText = data?.mimeType?.startsWith('text/');

  /* Text has no viewer of its own in a blob URL — the browser downloads it or
     shows it raw in a new tab — so it is read here and laid out as a document. */
  useEffect(() => {
    if (!data || !isText) { setText(''); return; }
    let alive = true;
    fetch(data.blobUrl)
      .then((r) => r.text())
      .then((t) => { if (alive) setText(t); })
      .catch(() => { if (alive) setText('Could not read this document.'); });
    return () => { alive = false; };
  }, [data, isText]);

  if (!data) return null;

  const isImage = data.mimeType?.startsWith('image/');
  const isPDF = data.mimeType === 'application/pdf';

  return createPortal(
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
      zIndex: 10000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px'
    }}>
      <div style={{
        width: '100%', maxWidth: '900px', background: 'var(--n-0)', borderRadius: '12px',
        display: 'flex', flexDirection: 'column', height: '90vh', overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{ padding: '15px 20px', borderBottom: '1px solid var(--n-100)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <strong style={{ fontSize: '16px', color: 'var(--n-800)' }}>{data.filename}</strong>
          <button 
            onClick={() => {
              URL.revokeObjectURL(data.blobUrl); // let the browser reclaim the blob
              setData(null);
            }}
            style={{ background: 'var(--danger-500)', color: 'var(--n-0)', border: 'none', padding: '6px 15px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}
          >
            Close
          </button>
        </div>

        {/* Content Area */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--n-50)', overflow: 'auto' }}>
          {isImage ? (
            <img src={data.blobUrl} alt="Preview" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          ) : isText ? (
            <pre style={{ width: '100%', height: '100%', margin: 0, padding: '28px 32px', overflow: 'auto', background: 'var(--n-0)', color: 'var(--n-800)', fontFamily: 'inherit', fontSize: 14, lineHeight: 1.8, whiteSpace: 'pre-wrap', wordBreak: 'break-word', textAlign: 'left' }}>
              {text}
            </pre>
          ) : isPDF ? (
            <iframe src={data.blobUrl} title="PDF Preview" style={{ width: '100%', height: '100%', border: 'none' }} />
          ) : (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <p>Preview not available for this file type.</p>
              {/* The escape hatch is a download, so it is withheld for a file
                  that was opened read-only — a policy HR has not released. */}
              {data.allowDownload === false ? (
                <p style={{ color: 'var(--n-500)', fontSize: 13, margin: 0 }}>
                  This document is read-only. Ask HR if you need a copy.
                </p>
              ) : (
                <a href={data.blobUrl} download={data.filename} style={{ color: 'var(--clr-accent)', fontWeight: 'bold' }}>Download to View</a>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default FilePreviewModal;